<template>
    <div>
        <b-tabs :nav-class="tabsNavClass">
            <b-tab title="Sitting Related Worksheets" :active="(initialTab == 'Court')" v-if="canViewCourtRelatedWorkItems" v-on:click="tabActivated(court)">
                <filter-ui :fields="getFilterFields('court')" ref="workitemsCourtFilter" :apiBaseUrl="apiBaseUrl" relatedTo="workitem-court" :filter="court.initialFilter" v-on:change="courtFilterChanged"></filter-ui>
                <ejs-grid id="workItemsCourtGrid"
                          ref="courtGrid"
                          :dataSource="court.gridData"
                          :toolbar="toolbar"
                          :enableStickyHeader="true"
                          :allowSorting="true"
                          :sortSettings="court.sortSettings"
                          :allowGrouping="false"
                          :groupSettings="groupSettings"
                          :allowResizing="true"
                          :allowPaging="true"
                          :pageSettings="pageSettings"
                          :showColumnChooser="true"
                          :allowExcelExport="true"
                          :dataStateChange="courtGridDataStateChanged"
                          :queryCellInfo="customiseCell"
                          :excelQueryCellInfo="customiseCell"
                          :rowSelected="courtGridRowSelectionChanged"
                          :rowDeselected="courtGridRowSelectionChanged"
                          :toolbarClick="courtGridToolbarClicked"
                          :rowDataBound="rowDataBound"
                          :created="gridCreated(court)"
                          v-if="court.show"
                          :headerCellInfo="headerCellInfoEvent"
                          :contextMenuItems="contextMenuItems"
                          :contextMenuClick="contextMenuClicked"
                          :contextMenuOpen="contextMenuOpened">
                    <e-columns>
                        <!--cut from ejs-grid-->
                        <!---->

                        <e-column field="epiqReferenceGridDisplay" headerText="Epiq #" width="100"></e-column>
                        <e-column field="courtMatterNumber" :headerText="$t('Vue.Grid.Shared.HeaderText.MatterNumber')"></e-column>
                        <e-column field="courtMatterNameOrDescription" :headerText="$t('Vue.Grid.Shared.HeaderText.MatterNameOrDescription')"></e-column>
                        <e-column field="courtMatterType" headerText="Matter Type"></e-column>
                        <e-column field="courtSittingDateTime" headerText="Sitting Date/Time"></e-column>

                        <e-column field="courtRegistryJurisdictionName" headerText="Court/Jurisdiction"></e-column>
                        <e-column field="courtLocationRoomName" headerText="Court Location/Room"></e-column>
                        <e-column field="restrictedDisplayText" headerText="Restricted Mode"></e-column>
                        <e-column field="mandatoryRequestDisplayText" headerText="Mandatory Request"></e-column>
                        <e-column field="workItemTypeDisplayName" headerText="Type" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="requiredDateTimeUTC" headerText="Required/Due" v-if="canViewTranscriptDueDateTime"></e-column>
                        <e-column field="requiredOrTurnsDueDateTimeUTC" headerText="Required/Due" v-if="!canViewTranscriptDueDateTime"></e-column>
                        <e-column field="turnsDueDateTimeUTC" headerText="Turns Due" v-if="canViewTranscriptDueDateTime"></e-column>
                        <e-column field="expectedNumberOfAudioSeconds" headerText="Length" :template="audioLengthColumnTemplate" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="assignedStaffInitials" headerText="Assigned Staff" :template="assignedStaffColumnTemplate" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="relatedOrders" headerText="Client Ref(s)" :template="relatedOrderClientReferencesColumnTemplate" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="turnaround" headerText="Turnaround" :allowSorting="false" :allowGrouping="false" v-if="canViewTurnaround"></e-column>

                        <e-column field="mergedByUserAccountDisplayName" headerText="Merged By" allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="completedByUserAccountDisplayName" headerText="Completed By" :allowSorting="false" :allowGrouping="false"></e-column>

                        <e-column field="statusDisplayName" headerText="Status" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="subStatusDeliveryState" headerText="Delivery State" :template="relatedOrderClientReferencesColumnTemplate" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="portalUploadStatus" :headerText="$t('Vue.Grid.Shared.HeaderText.PortalUploadStatus')" :template="portalUploadStatusColumnTemplate" :allowSorting="false" :allowGrouping="false" v-if="courtPortalUploadStatusVisible"></e-column>
                    </e-columns>
                </ejs-grid>
            </b-tab>

            <b-tab title="Interview Related Worksheets" :active="(initialTab == 'Interview')" v-if="canViewInterviewRelatedWorkItems" v-on:click="tabActivated(interview)">
                <filter-ui :fields="getFilterFields('interview')" ref="workitemsInterviewFilter" :apiBaseUrl="apiBaseUrl" relatedTo="workitem-interview" :filter="interview.initialFilter" v-on:change="interviewFilterChanged"></filter-ui>
                <ejs-grid id="workItemsInterviewGrid"
                          ref="interviewGrid"
                          :dataSource="interview.gridData"
                          :toolbar="toolbar"
                          :enableStickyHeader="true"
                          :allowSorting="true"
                          :sortSettings="interview.sortSettings"
                          :allowGrouping="false"
                          :groupSettings="groupSettings"
                          :allowResizing="true"
                          :allowPaging="true"
                          :pageSettings="pageSettings"
                          :showColumnChooser="true"
                          :allowExcelExport="true"
                          :dataStateChange="interviewGridDataStateChanged"
                          :queryCellInfo="customiseCell"
                          :excelQueryCellInfo="customiseCell"
                          :rowSelected="interviewGridRowSelectionChanged"
                          :rowDeselected="interviewGridRowSelectionChanged"
                          :toolbarClick="interviewGridToolbarClicked"
                          :rowDataBound="rowDataBound"
                          :created="gridCreated(interview)"
                          v-if="interview.show"
                          :headerCellInfo="headerCellInfoEvent"
                          :contextMenuItems="contextMenuItems"
                          :contextMenuClick="contextMenuClicked"
                          :contextMenuOpen="contextMenuOpened">
                    <e-columns>
                        <e-column field="epiqReference" headerText="Epiq #" width="100"></e-column>
                        <e-column field="interviewOrganisationName" headerText="Interview Organisation"></e-column>
                        <e-column field="interviewDateTime" headerText="Date/Time of Interview"></e-column>
                        <e-column field="interviewLocation" headerText="Location"></e-column>
                        <e-column field="interviewMatterOrCaseReference" headerText="Matter/Case Reference"></e-column>
                        <e-column field="interviewersDisplayText" headerText="Interviewer/s" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="intervieweesDisplayText" headerText="Interviewee/s" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="workItemTypeDisplayName" headerText="Type" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="requiredDateTimeUTC" headerText="Required/Due" v-if="canViewTranscriptDueDateTime"></e-column>
                        <e-column field="requiredOrTurnsDueDateTimeUTC" headerText="Required/Due" v-if="!canViewTranscriptDueDateTime"></e-column>
                        <e-column field="turnsDueDateTimeUTC" headerText="Turns Due" v-if="canViewTranscriptDueDateTime"></e-column>
                        <e-column field="expectedNumberOfAudioSeconds" headerText="Length" :template="audioLengthColumnTemplate" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="assignedStaffInitials" headerText="Assigned Staff" :template="assignedStaffColumnTemplate" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="relatedOrders" headerText="Client Ref(s)" :template="relatedOrderClientReferencesColumnTemplate" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="turnaround" headerText="Turnaround" :allowSorting="false" :allowGrouping="false" v-if="canViewTurnaround"></e-column>
                        <e-column field="statusDisplayName" headerText="Status" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="subStatusDeliveryState" headerText="Delivery State" :template="relatedOrderClientReferencesColumnTemplate" :allowSorting="false" :allowGrouping="false"></e-column>
                        <e-column field="portalUploadStatus" :headerText="$t('Vue.Grid.Shared.HeaderText.PortalUploadStatus')" :template="portalUploadStatusColumnTemplate" :allowSorting="false" :allowGrouping="false" v-if="interviewPortalUploadStatusVisible"></e-column>
                    </e-columns>
                </ejs-grid>
            </b-tab>

        </b-tabs>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import { GridPlugin, ColumnChooser, Resize, Sort, Group, Page, Toolbar, Search, ExcelExport, ContextMenu } from '@syncfusion/ej2-vue-grids';
    import AssignedStaffColumnTemplate from './AssignedStaffColumnTemplate';
    import AudioLengthColumnTemplate from './AudioLengthColumnTemplate';
    import RelatedOrderClientReferencesColumnTemplate from './RelatedOrderClientReferencesColumnTemplate';
    import { DataManager } from "@syncfusion/ej2-data";
    import axios from 'axios';
    import FilterUI from './FilterUI';
    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import GridMixins from '../../mixins/GridMixins';

    Vue.use(BootstrapVue);
    Vue.use(GridPlugin);

    //this is required to make the getGroupCaption function available globally and thus within the groupCaptionTemplate
    var workItemsGrid_vueComponent = null;
    function workItemsGrid_getGroupCaption(field, headerText, value) {
        return workItemsGrid_vueComponent.getGroupCaption(field, headerText, value);
    }
    window.workItemsGrid_getGroupCaption = workItemsGrid_getGroupCaption;

    export default {
        name: 'WorkItemsGrid',
        mixins: [ErrorsMixin, DateTimeMixin, GridMixins],
        components: {
            'filter-ui': FilterUI,
            //'notification-pop-over-template': NotificationPopOverTemplate,
        },
        provide: {
            grid: [ColumnChooser, Resize, Sort, Group, Page, Toolbar, Search, ExcelExport, ContextMenu]
        },
        props: {
            apiBaseUrl: {
                type: String,
                required: true
            },
            pagesBaseUrl: {
                type: String,
                required: true
            },
            canViewCourtRelatedWorkItems: {
                type: Boolean,
                default: false
            },
            canViewInterviewRelatedWorkItems: {
                type: Boolean,
                default: false
            },
            canViewOrders: {
                type: Boolean,
                default: false
            },
            canViewTranscriptDueDateTime: {
                type: Boolean,
                default: false,
            },
            canViewTurnaround: {
                type: Boolean,
                default: false,
            },
            courtPortalUploadStatusVisible: {
                type: Boolean,
                default: false,
            },
            interviewPortalUploadStatusVisible: {
                type: Boolean,
                default: false,
            },
            allWorkItemStatusOptions: {
                type: Array,
                required: true
            },
            allWorkItemTypeOptions: {
                type: Array,
                required: true
            },
            allCourtRegistryOptions: {
                type: Array,
                default: null
            },
            allCourtJurisdictionOptions: {
                type: Array,
                default: null
            },
            allCourtLocationOptions: {
                type: Array,
                default: null
            },
            allInterviewOrganisationOptions: {
                type: Array,
                default: null
            },
            allStaffAssignmentOptions: {
                type: Array,
                default: null
            },
            initialAssignedStaffId: {
                type: String,
                default: null
            },
            courtOrderOptions: {
                type: Array,
                default: null
            },
            initialCourtOrderId: {
                type: String,
                default: null
            },
            interviewOrderOptions: {
                type: Array,
                default: null
            },
            initialInterviewOrderId: {
                type: String,
                default: null
            },
            initialTab: {
                type: String,
                default: null
            }
        },
        data: function () {

            return {

                assignedStaffColumnTemplate: function () {
                    return { template: AssignedStaffColumnTemplate }
                },
                audioLengthColumnTemplate: function () {
                    return { template: AudioLengthColumnTemplate }
                },


                relatedOrderClientReferencesColumnTemplate: function () {
                    return { template: RelatedOrderClientReferencesColumnTemplate }
                },
                portalUploadStatusColumnTemplate: function () {
                    return {
                        template: Vue.component('columnTemplate', {
                            template: `<span :style="style">{{ text }}</span>`,
                            computed: {
                                style: function () {
                                    if (this.data.portalUploadStatus == true) return "color:green;";
                                    if (this.data.portalUploadStatus == false) return "color:red;";
                                    return null;
                                },
                                text: function () {
                                    if (this.data.portalUploadStatus == true) return this.$t("Entities.PortalUploadStatus.Uploaded");
                                    if (this.data.portalUploadStatus == false) return this.$t("Entities.PortalUploadStatus.NotUploaded");
                                    return null;
                                }
                            }
                        })
                    }
                },

                groupSettings: { showGroupedColumn: true, disablePageWiseAggregates: true, captionTemplate: "${workItemsGrid_getGroupCaption(field, headerText, key)}" },
                pageSettings: { pageSize: 10, pageSizes: [5, 10, 25, 50] },

                court: {
                    name: "court",
                    initialized: false,
                    dataLoaded: false,
                    show: true,
                    quickFilters: null,
                    initialFilter: null,

                    sortSettings: { columns: [{ field: this.canViewTranscriptDueDateTime ? "requiredDateTimeUTC" : "requiredOrTurnsDueDateTimeUTC", direction: "Ascending" }] },
                    gridDataState: {
                        filter: null,
                        search: null,
                        sorted: [{ name: this.canViewTranscriptDueDateTime ? "requiredDateTimeUTC" : "requiredOrTurnsDueDateTimeUTC", direction: "ascending" }],
                        group: null,
                        skip: 0,
                        take: 10,
                        requiresCounts: true
                    },
                    gridData: { result: [], count: 0 },
                    grid: null
                },

                interview: {
                    name: "interview",
                    initialized: false,
                    dataLoaded: false,
                    show: true,
                    quickFilters: null,
                    initialFilter: null,

                    sortSettings: { columns: [{ field: this.canViewTranscriptDueDateTime ? "requiredDateTimeUTC" : "requiredOrTurnsDueDateTimeUTC", direction: "Ascending" }] },
                    gridDataState: {
                        filter: null,
                        search: null,
                        sorted: [{ name: this.canViewTranscriptDueDateTime ? "requiredDateTimeUTC" : "requiredOrTurnsDueDateTimeUTC", direction: "ascending" }],
                        group: null,
                        skip: 0,
                        take: 10,
                        requiresCounts: true
                    },
                    gridData: { result: [], count: 0 },
                    grid: null
                },

                columnFormats: {
                    courtSittingDateTime: {
                        type: 'datetime',
                        options: {
                            mode: 'checktimeincludeseconds',
                            outputAsUTC: true
                        }
                    },
                    interviewDateTime: {
                        type: 'datetime',
                        options: {
                            mode: 'checktimeincludeseconds',
                            outputAsUTC: true
                        }
                    },
                    requiredDateTimeUTC: {
                        type: 'datetime',
                        options: {
                            mode: 'checktime'
                        }
                    },
                    requiredOrTurnsDueDateTimeUTC: {
                        type: 'datetime',
                        options: {
                            mode: 'checktime'
                        }
                    },
                    turnsDueDateTimeUTC: {
                        type: 'datetime',
                        options: {
                            mode: 'checktime'
                        }
                    }
                },
                contextMenuItems: [
                    {
                        text: "Copy", iconCss: "fas fa-copy", items: [
                            { text: "Worksheet Link", iconCss: "fas fa-link", id: "copyworksheetlink" },
                            { text: "EQ#", iconCss: "fas fa-qrcode", id: "copyeqnumber" },
                            { text: "Matter #", iconCss: "fas fa-file-alt", id: "copymatternumber" },
                            { text: "Matter Name", iconCss: "fas fa-file-alt", id: "copymattername" },
                            { text: "EQ + Matter Details", iconCss: "fas fa-align-left", id: "copymatterdetails" },
                        ],
                    },
                ],
            }
        },

        computed: {
            tabsNavClass: function () {
                var tabsNavClass = "border-bottom-0";
                if (!this.canViewCourtRelatedWorkItems || !this.canViewInterviewRelatedWorkItems) tabsNavClass += " d-none";
                return tabsNavClass;
            },
            toolbar: function () {
                var toolbarItems = this.defaultEJ2DataGridToolbarItems();
                toolbarItems =
                    toolbarItems.concat(
                        [
                            { text: "Open", tooltipText: "Open selected Worksheet Details", prefixIcon: "fas fa-file-alt", id: "workitemdetails", cssClass: "workitemdetails" }
                        ]
                    );
                if (this.canViewOrders) {
                    toolbarItems =
                        toolbarItems.concat(
                            [
                                { text: "Orders", tooltipText: "View Orders related to selected Worksheet", prefixIcon: "fas fa-list", id: "workitemorders", cssClass: "orderworksheets" }
                            ]
                        );
                }
                return toolbarItems;
            }
        },
        methods: {
            contextMenuOpened: function (args) {
                var data = args.rowInfo.rowData;
                var hasItems = false;
                var contextMenuObj = null;
                if (data.relatedTo.interview != undefined) {
                    contextMenuObj = this.$refs.interviewGrid.ej2Instances.contextMenuModule.contextMenu;
                } else if (data.relatedTo.courtSitting != undefined) {
                    contextMenuObj = this.$refs.courtGrid.ej2Instances.contextMenuModule.contextMenu;
                } else {
                    args.cancel = true;
                    return;
                }

                var worksheetlink = document.location.origin + this.pagesBaseUrl + '/WorkItems/Details/' + data.workItemId;

                var menuItems = [
                    {
                        menuItem: "copyworksheetlink",
                        requiredStrings: [worksheetlink]
                    },
                    {
                        menuItem: "copyeqnumber",
                        requiredStrings: [data.epiqReferenceGridDisplay]
                    },
                    {
                        menuItem: "copymatternumber",
                        requiredStrings: [data.courtMatterNumber]
                    },
                    {
                        menuItem: "copymattername",
                        requiredStrings: [data.courtMatterNameOrDescription]
                    },
                    {
                        menuItem: "copymatterdetails",
                        requiredStrings: [data.epiqReferenceGridDisplay, data.courtMatterNumber, data.courtMatterNameOrDescription],
                        allRequired: true
                    }
                ];

                for (var i = 0; i < menuItems.length; i++) {
                    var matchCount = 0;
                    for (var j = 0; j < menuItems[i].requiredStrings.length; j++) {
                        if (menuItems[i].requiredStrings[j] != null && menuItems[i].requiredStrings[j].trim() != "")
                            matchCount++;
                    }
                    if (menuItems[i].allRequired) {
                        if (matchCount < menuItems[i].requiredStrings.length)
                            contextMenuObj.hideItems([menuItems[i].menuItem], true);
                        else {
                            contextMenuObj.showItems([menuItems[i].menuItem], true);
                            hasItems = true;
                        }
                    }
                    else if (matchCount == 0)
                        contextMenuObj.hideItems([menuItems[i].menuItem], true);
                    else {
                        contextMenuObj.showItems([menuItems[i].menuItem], true);
                        hasItems = true;
                    }
                }

                if (!hasItems) args.cancel = true;
            },

            contextMenuClicked: function (args) {
                var data = args.rowInfo.rowData;
                var worksheetlink = document.location.origin + this.pagesBaseUrl + '/WorkItems/Details/' + data.workItemId;
                switch (args.item.id) {
                    case "copyworksheetlink":
                        this.copyToClipboard(worksheetlink);
                        break;
                    case "copyeqnumber":
                        this.copyToClipboard(data.epiqReferenceGridDisplay);
                        break;
                    case "copymatternumber":
                        this.copyToClipboard(data.courtMatterNumber);
                        break;
                    case "copymattername":
                        this.copyToClipboard(data.courtMatterNameOrDescription);
                        break;
                    case "copymatterdetails":
                        this.copyToClipboard("HEARING COMMENCED " + data.epiqReferenceGridDisplay + "\n\nMatter Number: " + data.courtMatterNumber + "\n\nMatter Name: " + data.courtMatterNameOrDescription);
                        break;

                }
            },
            copyToClipboard: function (args) {
                navigator.clipboard.writeText(args);
                this.$bvToast.toast(args, {
                    title: 'Copied to Clipboard',
                    variant: 'success',
                    appendToast: true
                });
            },
            getFilterFields: function (gridName) {
                var filterFields = [];
                if (gridName == "court") {
                    filterFields.push(
                        {
                            field: "SittingDate",
                            label: "Sitting Date",
                            type: "date",
                            format: "dd/MM/yyyy"
                        },
                        {
                            field: "CourtRegistryId",
                            label: "Court/Tribunal/Other",
                            type: "multiselect",
                            options: this.allCourtRegistryOptions,
                            placeholder: "Select Court/Tribunal/Other Values"
                        },
                        {
                            field: "CourtJurisdictionId",
                            label: "Court/Jurisdiction",
                            type: "multiselect",
                            options: this.allCourtJurisdictionOptions,
                            placeholder: "Select Jurisdictions"
                        },
                        {
                            field: "CourtLocationId",
                            label: "Court Location",
                            type: "multiselect",
                            options: this.allCourtLocationOptions,
                            placeholder: "Select Locations"
                        },
                        {
                            field: "Restricted",
                            label: "Restricted Mode",
                            type: "boolean",
                            options: [{ value: false, text: "Not Restricted" }, { value: true, text: "Restricted" }]
                        },
                        {
                            field: "MandatoryRequest",
                            label: "Mandatory Request",
                            type: "boolean",
                            options: [{ value: true, text: "Mandatory Request" }, { value: false, text: "Not Mandatory" }]
                        }
                    );
                }
                if (gridName == "interview") {
                    filterFields.push(
                        {
                            field: "InterviewDate",
                            label: "Date of Interview",
                            type: "date",
                            format: "dd/MM/yyyy"
                        },
                        {
                            field: "InterviewOrganisationId",
                            label: "Interview Organisation",
                            type: "multiselect",
                            options: this.allInterviewOrganisationOptions,
                            placeholder: "Select Interview Oganisations"
                        }
                    );
                }
                filterFields.push(
                    {
                        field: "WorkItemType",
                        label: "Type",
                        type: "multiselect",
                        options: this.allWorkItemTypeOptions,
                        placeholder: "Select Worksheet Types"
                    },
                    {
                        field: "RequiredDate",
                        label: "Required/Due Date",
                        type: "date",
                        format: "dd/MM/yyyy"
                    },
                    {
                        field: "CompletedDate",
                        label: "Date Delivered",
                        type: "date",
                        format: "dd/MM/yyyy"
                    },
                    {
                        field: "Status",
                        label: "Status",
                        type: "multiselect",
                        options: this.allWorkItemStatusOptions,
                        placeholder: "Select Status Values"
                    }
                );
                if ((this.courtPortalUploadStatusVisible && (gridName == "court")) ||
                    (this.interviewPortalUploadStatusVisible && (gridName == "interview"))) {
                    filterFields.push(
                        {
                            field: "PortalUploadStatus",
                            label: this.$t("Vue.Grid.Shared.HeaderText.PortalUploadStatus"),
                            type: "boolean",
                            options: [{ value: false, text: this.$t("Entities.PortalUploadStatus.NotUploaded") }, { value: true, text: this.$t("Entities.PortalUploadStatus.Uploaded") }]
                        }
                    );
                }
                filterFields.push(
                    {
                        field: "OrderId",
                        label: "Order Number",
                        type: "multiselect",
                        options: this.courtOrderOptions
                    },
                    {
                        field: "AssignedStaffId",
                        label: "Assigned To",
                        type: "multiselect",
                        options: this.allStaffAssignmentOptions
                    }
                );
                return filterFields;
            },

            rowDataBound: function (args) {
                if (args.data['completedDateTimeUTC'] == null) { //if the item has been completed then we do not do any due today/overdue coloring
                    var requiredDateTime = args.data['requiredDateTimeUTC'];
                    var dueDate = this.formatDateTime(requiredDateTime, 'YYYY-MM-DD', false, false);
                    var requiredDateTimeUTCString = this.formatDateTime(requiredDateTime, 'YYYY-MM-DDTHH:mm:ss', false, true);
                    if (requiredDateTimeUTCString.indexOf('00:00:01') > 0) { //if the required date/time is 1 second past midnight UTC then that actually means there is no specific time, just the date
                        dueDate = this.formatDateTime(requiredDateTimeUTCString, 'YYYY-MM-DD', true, true);
                    }
                    var todaysDate = this.formatDateTime(this.now(), 'YYYY-MM-DD', false, false);
                    if (dueDate == todaysDate) {
                        // setting row color to yellow - warning
                        args.row.classList.add('worksheet-due-today');
                    } else if (requiredDateTime < this.now()) {
                        args.row.classList.add('worksheet-overdue');
                    }
                }
            },

            getGroupCaption: function (field, headerText, value) {
                if (field == "courtSittingDateTime") value = this.dateTimeDisplayString(value, 'checktimeincludeseconds', true, true);
                else if (field == "interviewDateTime") value = this.dateTimeDisplayString(value, 'checktimeincludeseconds', true, true);
                else if ((field == "requiredDateTimeUTC") || (field == "requiredOrTurnsDueDateTimeUTC") || (field == "turnsDueDateTimeUTC")) value = this.dateTimeDisplayString(value, 'checktime', true, false);
                return headerText + ": " + value;
            },
            customizeGroupKeys(groupedDate) {
                groupedDate.forEach(g => {
                    if (g.field && g.key && g.items) {
                        if (g.field == "courtSittingDateTime") g.key = this.dateTimeDisplayString(g.key, 'checktimeincludeseconds', true, true);
                        else if (g.field == "interviewDateTime") g.key = this.dateTimeDisplayString(g.key, 'checktimeincludeseconds', true, true);
                        else if (g.field == "requiredDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'checktime', false);
                        else if (g.field == "requiredOrTurnsDueDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'checktime', true, false);
                        else if (g.field == "turnsDueDateTimeUTC") g.key = this.dateTimeDisplayString(g.key, 'checktime', true, false);
                        this.customizeGroupKeys(g.items);
                    }
                });
            },
            customiseCell: function (args) {
                if ((args.column.field == 'epiqReferenceCourtMatterNumber') && (args.name == 'queryCellInfo')) {
                    args.cell.innerHTML = "<a href='" + this.pagesBaseUrl + '/WorkItems/Details/' + args.data.workItemId + "' target='_blank'>" + args.data.epiqReferenceCourtMatterNumber + "</a>";
                } else if ((args.column.field == 'epiqReference') && (args.name == 'queryCellInfo')) {
                    args.cell.innerHTML = "<a href='" + this.pagesBaseUrl + '/WorkItems/Details/' + args.data.workItemId + "' target='_blank'>" + args.data.epiqReference + "</a>";
                } else if ((args.column.field == 'epiqReferenceGridDisplay') && (args.name == 'queryCellInfo')) {
                    args.cell.innerHTML = "<a href='" + this.pagesBaseUrl + '/WorkItems/Details/' + args.data.workItemId + "' target='_blank'>" + args.data.epiqReferenceGridDisplay + "</a>";
                } else if ((args.column.field === 'restrictedDisplayText') && (args.name != 'excelQueryCellInfo')) {
                    var restricted = args.data['restricted'];
                    if (restricted === true) {
                        args.cell.classList.add('worksheet-restricted');
                    }
                } else {
                    this.defaultEJ2DataGridQueryCellInfo(args, this.columnFormats);
                }
            },

            courtFilterChanged: function(filter) {
                this.filterChanged(this.court, filter);
            },
            courtGridDataStateChanged: function (args) {
                this.gridDataStateChanged(this.court, args);
            },
            courtGridRowSelectionChanged: function (args) {
                this.rowSelectionChanged(this.court.grid, args);
            },
            courtGridToolbarClicked: function (args) {
                this.toolbarClicked(this.court, args);
            },
            interviewFilterChanged: function (filter) {
                this.filterChanged(this.interview, filter);
            },
            interviewGridDataStateChanged: function (args) {
                this.gridDataStateChanged(this.interview, args);
            },
            interviewGridRowSelectionChanged: function (args) {
                this.rowSelectionChanged(this.interview.grid, args);
            },
            interviewGridToolbarClicked: function (args) {
                this.toolbarClicked(this.interview, args);
            },

            filterChanged: function (relatedTo, filter) {
                relatedTo.gridDataState.filter = filter;
                if (relatedTo.initialized) relatedTo.grid.refresh();
            },
            gridDataStateChanged: function (relatedTo, args) {
                relatedTo.gridDataState.search = args.search;
                relatedTo.gridDataState.sorted = args.sorted;
                relatedTo.gridDataState.group = args.group;
                relatedTo.gridDataState.skip = args.skip;
                relatedTo.gridDataState.take = args.take;
                relatedTo.gridDataState.requiresCounts = args.requiresCounts;
                if (relatedTo.initialized) {
                    relatedTo.grid.showSpinner();
                    this.loadWorkItems(relatedTo.name, relatedTo.gridDataState)
                        .then(result => {
                            if (result) {
                                relatedTo.gridData = result;
                            }
                            relatedTo.grid.hideSpinner();
                        });
                }
            },
            rowSelectionChanged: function (grid, args) {
                if (args.name == 'rowSelected') {
                    this.updateToolbarItems(grid, grid.getSelectedRecords()[0])
                } else {
                    this.updateToolbarItems(grid, null)
                }
            },
            updateToolbarItems: function(grid, workItem) {
                this.setToolbarItemState(grid, 'workitemdetails', (workItem != null));
                this.setToolbarItemState(grid, 'workitemorders', (workItem != null));
            },
            setToolbarItemState: function(grid, itemClass, enabled) {
                grid.toolbarModule.enableItems([itemClass], enabled);
            },
            toolbarClicked: function (relatedTo, args) {
                if ((args.item.id === 'workitemdetails') || (args.item.id === 'workitemorders')) {
                    this.handleAction(args.item.id, relatedTo.grid.getSelectedRecords()[0]);
                } else if (args.item.id === 'excelexport') {
                    //to properly export all data (and not just the entries displayed on the current page) we have to load everything first
                    //we do this by taking the current grid state (which includes the current filter and search and sort settings)
                    var gridDataState = {
                        ...relatedTo.gridDataState
                    };
                    //but reset the skip and take to 0 which will return everything and unset the requiresCounts cause we are not interseted in the totals
                    gridDataState.requiresCounts = false;
                    relatedTo.grid.showSpinner();
                    this.loadWorkItems(relatedTo.name, gridDataState)
                        .then(result => {
                            //the result is either false if something failed during the axios request, or it will contain the list in the result property which is what we have to pass to the actual export
                            if (result) {
                                var query = relatedTo.grid.getDataModule().generateQuery(true);
                                var groupedData = new DataManager(result.result).executeLocal(query);
                                this.customizeGroupKeys(groupedData);
                                relatedTo.grid.excelExport({ dataSource: groupedData });
                            }
                            relatedTo.grid.hideSpinner();
                        });
                } else if (args.item.id === 'reset') {
                    this.deleteGridSettings(relatedTo.grid)
                        .then(() => {
                            relatedTo.gridDataState.search = "";
                            relatedTo.show = false;
                            Vue.nextTick(() => {
                                relatedTo.show = true;
                                Vue.nextTick(() => {
                                    relatedTo.grid = (relatedTo.name == "court") ? this.$refs.courtGrid.ej2Instances : this.$refs.interviewGrid.ej2Instances;
                                    this.setupGridPersistence(relatedTo.grid);
                                    this.updateToolbarItems(relatedTo.grid, null);
                                    relatedTo.grid.refresh();
                                });
                            });
                        });
                } else {
                    this.defaultEJ2DataGridToolbarClickHandler(args, relatedTo.grid);
                }
            },
            handleAction: function (action, workItem) {
                if (workItem) {
                    if (action === 'workitemdetails') {
                        window.open(this.pagesBaseUrl + '/WorkItems/Details/' + workItem.workItemId);
                    } else if (action === 'workitemorders') {
                        window.open(this.pagesBaseUrl + '/Orders/' + workItem.workItemId);
                    }
                }
            },
            loadWorkItems: function (relatedToName, gridDataState) {
                var url = this.apiBaseUrl + '/workitem/list/' + relatedToName;
                return axios
                    .post(url, JSON.stringify(gridDataState), {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    .then(response => {
                        var gridData = response.data;
                        if (gridData && gridData.result) {
                            gridData.result = gridData.result.map((workItem) => {
                                if (workItem.courtSittingDateTime) workItem.courtSittingDateTime = this.parseDateTime(workItem.courtSittingDateTime, true, true);
                                if (workItem.interviewDateTime) workItem.interviewDateTime = this.parseDateTime(workItem.interviewDateTime, true, true);
                                if (workItem.requiredDateTimeUTC) workItem.requiredDateTimeUTC = this.parseDateTime(workItem.requiredDateTimeUTC, true, false);
                                if (workItem.turnsDueDateTimeUTC) workItem.turnsDueDateTimeUTC = this.parseDateTime(workItem.turnsDueDateTimeUTC, true, false);
                                if (workItem.turnsDueDateTimeUTC) workItem.requiredOrTurnsDueDateTimeUTC = this.parseDateTime(workItem.turnsDueDateTimeUTC, true, false);
                                else if (workItem.requiredDateTimeUTC) workItem.requiredOrTurnsDueDateTimeUTC = this.parseDateTime(workItem.requiredDateTimeUTC, true, false);
                                else workItem.requiredOrTurnsDueDateTimeUTC = null;
                                return workItem;
                            });
                        }
                        return gridData;
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                        return false;
                    });
            },
            tabActivated: function (relatedTo) {
                if (!relatedTo.dataLoaded) {
                    relatedTo.grid.refresh();
                    relatedTo.dataLoaded = true;
                }
            },
            gridCreated: function (relatedTo) {
                Vue.nextTick(() => {
                    this.defaultEJ2DataGridCreatedHandler(relatedTo.grid);
                });
            }
        },
        mounted: function () {
            workItemsGrid_vueComponent = this;
            if (this.canViewCourtRelatedWorkItems) {
                this.court.grid = this.$refs.courtGrid.ej2Instances;
                this.setupGridPersistence(this.court.grid);
                this.updateToolbarItems(this.court.grid, null);
                if (this.initialCourtOrderId) {
                    this.court.initialFilter = {
                        condition: "and",
                        rules: [
                            {
                                field: "OrderId",
                                operator: "in",
                                value: [this.initialCourtOrderId]
                            }
                        ]
                    }
                    this.court.gridDataState.filter = this.court.initialFilter;
                    this.court.gridDataState.search = "";
                    this.court.grid.search("");
                } else if (this.initialAssignedStaffId) {
                    this.court.initialFilter = {
                        condition: "and",
                        rules: [
                            {
                                field: "AssignedStaffId",
                                operator: "in",
                                value: [this.initialAssignedStaffId]
                            },
                            {
                                field: "Status",
                                operator: "notin",
                                value: ["Completed"]
                            }
                        ]
                    }
                    this.court.gridDataState.filter = this.court.initialFilter;
                    this.court.gridDataState.search = "";
                    this.court.grid.search("");
                }
                this.$refs.workitemsCourtFilter.loadQuickFilters()
                    .then(() => {
                        this.court.initialized = true;
                        if (this.initialTab == 'Court') {
                            setTimeout(() => {
                                this.court.grid.refresh();
                                this.court.dataLoaded = true;
                            }, 250);
                        }
                    });
            }
            if (this.canViewInterviewRelatedWorkItems) {
                this.interview.grid = this.$refs.interviewGrid.ej2Instances;
                this.setupGridPersistence(this.interview.grid);
                this.updateToolbarItems(this.interview.grid, null);
                if (this.initialInterviewOrderId) {
                    this.interview.initialFilter = {
                        condition: "and",
                        rules: [
                            {
                                field: "OrderId",
                                operator: "in",
                                value: [this.initialInterviewOrderId]
                            }
                        ]
                    }
                    this.interview.gridDataState.filter = this.interview.initialFilter;
                    this.interview.gridDataState.search = "";
                    this.interview.grid.search("");
                } else if (this.initialAssignedStaffId) {
                    this.interview.initialFilter = {
                        condition: "and",
                        rules: [
                            {
                                field: "AssignedStaffId",
                                operator: "in",
                                value: [this.initialAssignedStaffId]
                            },
                            {
                                field: "Status",
                                operator: "notin",
                                value: ["Completed"]
                            }
                        ]
                    }
                    this.interview.gridDataState.filter = this.interview.initialFilter;
                    this.interview.gridDataState.search = "";
                    this.interview.grid.search("");
                }
                this.$refs.workitemsInterviewFilter.loadQuickFilters()
                    .then(() => {
                        this.interview.initialized = true;
                        if (this.initialTab == 'Interview') {
                            setTimeout(() => {
                                this.interview.grid.refresh();
                                this.interview.dataLoaded = true;
                            }, 250);
                        }
                    });
            }
            console.log('initial filter ', JSON.stringify(this.court.initialFilter));
        }
    }
</script>
<style scoped>
    div >>> .worksheet-overdue {
        background-color: #F5C6CB !important;
    }
    div >>> .worksheet-due-today {
        background-color: #FFEEBA !important;
    }
    div >>> .worksheet-restricted {
        color: red !important;
    }
</style>